import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch, useAppStore } from '../store';
import { useFedStore } from '../fed/store';
import { Ic } from '../fed/icons';
import './ParticipantNotices.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5252/api';
export async function noticeRequest(path = '', method = 'GET') {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('auth');
  const response = await apiFetch(`${API}/participant-notices${path}`, {
    method, headers: { Authorization: `Bearer ${token}` }, cache: 'no-store',
  });
  if (!response.ok) throw new Error('request');
  return response.json();
}

export function ParticipantNoticeSheet({ notice, onDone, acknowledge = noticeRequest }) {
  const { lang, go } = useFedStore();
  const kz = lang === 'kz';
  const nickname = notice.kind === 'nickname';
  const dialog = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    const node = dialog.current;
    const trigger = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    node.showModal(); // Native focus trap + inert background, including Safari.
    return () => {
      node.close();
      document.body.style.overflow = previousOverflow;
      trigger?.focus?.({ preventScroll: true });
    };
  }, []);
  const finish = async (edit = false) => {
    if (busy) return;
    setBusy(true); setError(false);
    try {
      await acknowledge(`/${notice.id}/ack`, 'POST');
      onDone(notice.id);
      useAppStore.getState().fetchNotifications();
      if (edit) {
        go('tournament', { tourId: notice.tourId, tourTab: 'reg' });
        window.setTimeout(() => document.getElementById('cs2-tournament-panel')?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' }), 150);
      }
    } catch (_) { setError(true); setBusy(false); }
  };
  return createPortal(
    <dialog ref={dialog} className="participant-notice" aria-labelledby="participant-notice-title" aria-describedby="participant-notice-copy"
      onKeyDown={(event) => {
        if (event.key !== 'Tab') return;
        const buttons = [...event.currentTarget.querySelectorAll('button:not(:disabled)')];
        const first = buttons[0]; const last = buttons[buttons.length - 1];
        if (!buttons.length) event.preventDefault();
        else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }}
      onCancel={(event) => { event.preventDefault(); finish(); }}>
      <div className="participant-notice__body">
        <span className="participant-notice__icon"><Ic name={nickname ? 'info' : 'community'} size={28} /></span>
        <p className="participant-notice__eyebrow">NOVA DOTA 2 OPEN · 24–25.10.2026</p>
        <h2 id="participant-notice-title">{nickname ? (kz ? 'Ойын лақап атын өзгертіңіз' : 'Измените игровой никнейм') : (kz ? 'Команда құрамы толық!' : 'Команда в полном составе!')}</h2>
        <div id="participant-notice-copy">
          {nickname ? <>
            <p>{kz ? 'Ойын лақап атыңызда әдепсіз сөздер бар. Оны ойыншы профилінің баптауларында өзгертіңіз.' : 'Ваш игровой никнейм содержит нецензурное выражение. Пожалуйста, измените его в настройках профиля игрока.'}</p>
            <div className="participant-notice__callout">{kz ? 'Никнейм өзгертілмесе, турнирге қатысуға рұқсат берілмейді. Бұл талап регламенттің 1.11-тармағында көрсетілген.' : 'Без изменения никнейма вы не сможете быть допущены к участию в турнире. Это требование пункта 1.11 регламента.'}</div>
          </> : <>
            <p>{kz ? `«${notice.teamName}» командасының құрметті қатысушылары, негізгі бес ойыншы жиналды. Келесі қадам — NOVA клубында алдын ала тіркелу.` : `Уважаемые участники команды «${notice.teamName}», у вас собраны все пять основных игроков. Следующий шаг — предварительная регистрация в NOVA.`}</p>
            <div className="participant-notice__callout"><Ic name="pin" size={22} /><div><strong>{kz ? 'Турнир басталғанға дейін' : 'До начала турнира'}</strong><p>{kz ? 'Әр қатысушы, оның ішінде қосалқы ойыншы да, NOVA компьютерлік клубына жеке келіп, клуб аумағында өзінің ойын аккаунтын алдын ала тіркеуі қажет. Бұл турнирге толық қатысу үшін қажет.' : 'Каждому участнику, включая запасного, необходимо лично прийти в компьютерный клуб NOVA и предварительно зарегистрировать свой игровой аккаунт на территории клуба. Это необходимо для полного доступа к участию в турнире.'}</p></div></div>
            <p className="participant-notice__note">{kz ? 'Толық құрам өтінімнің расталғанын білдірмейді: қатысуды ұйымдастырушы растайды.' : 'Полный состав не означает подтверждение заявки: участие подтверждает организатор.'}</p>
          </>}
        </div>
        {error && <p role="alert">{kz ? 'Сақтау мүмкін болмады. Қайта көріңіз.' : 'Не удалось сохранить отметку. Попробуйте ещё раз.'}</p>}
      </div>
      <div className="participant-notice__actions">
        {nickname && <button className="participant-notice__primary" disabled={busy} onClick={() => finish(true)}>{kz ? 'Ойыншы профиліне' : 'Изменить игровой ник'}</button>}
        <button className={nickname ? '' : 'participant-notice__primary'} disabled={busy} onClick={() => finish()}>{busy ? (kz ? 'Сақталуда…' : 'Сохраняем…') : (kz ? 'Түсінікті' : 'Понятно')}</button>
      </div>
    </dialog>, document.body,
  );
}

function AccountNotices() {
  const [pending, setPending] = useState([]);
  const [current, setCurrent] = useState(null);
  const dismissed = useRef(new Set());
  useEffect(() => {
    let active = true; let fetching = false;
    const poll = async () => {
      if (fetching || document.hidden) return;
      fetching = true;
      try {
        const data = await noticeRequest();
        if (active) setPending((data.notices || []).filter(n => !dismissed.current.has(n.id)));
      } catch (_) { /* Retry quietly; never replace the page with an error. */ }
      finally { fetching = false; }
    };
    poll();
    const timer = setInterval(poll, 20000);
    window.addEventListener('focus', poll);
    window.addEventListener('participant-notices:refresh', poll);
    return () => { active = false; clearInterval(timer); window.removeEventListener('focus', poll); window.removeEventListener('participant-notices:refresh', poll); };
  }, []);
  useEffect(() => {
    if (current || !pending.length) return undefined;
    // Don't cover onboarding, permission prompts, the PDF reader or other dialogs.
    const timer = setInterval(() => {
      if (!document.querySelector('[role="dialog"],dialog[open],.modal-overlay') && !document.hidden) setCurrent(pending[0]);
    }, 1500);
    return () => clearInterval(timer);
  }, [pending, current]);
  const done = useCallback((id) => {
    dismissed.current.add(id);
    setPending(items => items.filter(n => n.id !== id));
    setCurrent(null);
  }, []);
  return current ? <ParticipantNoticeSheet key={current.id} notice={current} onDone={done} /> : null;
}

export default function ParticipantNotices() {
  const user = useAppStore(s => s.user);
  return user?.id && !user.needs_onboarding ? <AccountNotices key={user.id} /> : null;
}
